<script setup lang="ts">
import { ContentFileImageVariant, tryImageVariant } from '#imports'

const { image, slogan, subtitle, showLogoMark, breakOut, height = 500, darkOverlayOpacity = 30, textAlignment = 'left', maskBottomImage = false } = defineProps<{
  image: ContentFile
  slogan: string
  subtitle?: string
  showLogoMark?: boolean
  breakOut?: boolean
  height?: number
  darkOverlayOpacity?: number
  textAlignment?: 'left' | 'center' | 'right'
  // Apply a ellips-shaped mask to the bottom of the image
  maskBottomImage?: boolean
}>()

const maxWidthClass = inject<string>('maxWidthClass', 'max-w-7xl')

const flexClass = {
  left: 'items-start',
  center: 'items-center',
  right: 'items-end',
}[textAlignment]

const maskClass = computed(() => maskBottomImage ? 'mask-bottom-circle' : '')
</script>

<template>
  <ContentBlockViewPartialContainer
    class="relative w-full overflow-hidden"
    :style="{ height: `${height}px` }"
    :break-out="breakOut"
  >
    <div class="absolute inset-0" :class="maskClass">
      
      <img
        :src="tryImageVariant(image, ContentFileImageVariant.Large)"
        :alt="slogan"
        class="absolute inset-0 size-full object-cover"
      />
      
      <div
        class="absolute inset-0 bg-black"
        :style="{ opacity: `${darkOverlayOpacity}%` }"
        :class="maskClass"
      />
    </div>
   

    
    <div
      class="relative mx-auto flex h-full flex-col justify-center gap-y-4 px-4 text-white"
      :class="maxWidthClass, flexClass"
    >
      <PublicLogoMark v-if="showLogoMark" class="max-h-16" />

      
      <BaseHeading
        size="4xl"
        as="h2"
      >
        {{ slogan }}
      </BaseHeading>

      
      <div v-if="subtitle" class="w-12 h-2 bg-secondary-500" />

      
      <BaseText
        v-if="subtitle"
        size="xl"
        weight="medium"
      >
        {{ subtitle }}
      </BaseText>
    </div>
  </ContentBlockViewPartialContainer>
</template>

<style scoped>
/* We could probably replace these with tailwind classes after upgrading to tailwind v4 */
.mask-bottom-circle {
  mask-image: radial-gradient(ellipse 250% 200% at 50% 0%, black 50%, transparent 50%);
}

/* Media queries for responsive mask shape */
@media (max-width: 768px) {
  .mask-bottom-circle {
    /* Flatter curve for smaller screens */
    mask-image: radial-gradient(ellipse 350% 200% at 50% 0%, black 50%, transparent 50%);
  }
}

@media (max-width: 480px) {
  .mask-bottom-circle {
    /* Even flatter curve for mobile screens */
    mask-image: radial-gradient(ellipse 450% 200% at 50% 0%, black 50%, transparent 50%);
  }
}
</style>
